import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { T } from '../config/terminology';
import { isEducation } from '../config/product';
import { FEATURE_INTERVIEW_UI } from '../config/featureFlags';
import { useTrialExperience } from './TrialExperienceProvider';
import './Sidebar.css';

const SIDEBAR_PINNED_KEY = 'portiq_sidebar_pinned';
const SIDEBAR_WIDTH_EXPANDED = '260px';
const SIDEBAR_WIDTH_RAIL = '64px';

function readSidebarPinnedPref() {
  try {
    const pinned = window.localStorage.getItem(SIDEBAR_PINNED_KEY);
    if (pinned != null) return pinned === '1';
    const legacy = window.localStorage.getItem('portiq_sidebar_collapsed');
    if (legacy != null) return legacy !== '1';
    return false;
  } catch {
    return false;
  }
}

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trial = useTrialExperience();
  const profileLoading = Boolean(trial?.loading);
  const role = String(trial?.profile?.role || '').toLowerCase();
  const isEducationAdmin = isEducation && role !== 'faculty';
  const showLecturesNav = !isEducation || profileLoading || role === 'faculty';

  const isWorkplace = !isEducation;
  const [pinned, setPinned] = useState(readSidebarPinnedPref);
  const [railHovered, setRailHovered] = useState(false);

  const accountName = String(trial?.profile?.username || '').trim();
  const accountEmail = String(trial?.profile?.email || '').trim();
  const accountInitial = (accountName || accountEmail || 'U').charAt(0).toUpperCase();
  const accountRoleLabel = role === 'faculty' ? 'Teacher' : role === 'super_admin' ? 'Super Admin' : 'Admin';

  const sidebarExpanded = pinned || railHovered;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--sidebar-width',
      sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_RAIL
    );
    root.toggleAttribute('data-sidebar-rail', !pinned);
    root.toggleAttribute('data-sidebar-expanded', sidebarExpanded);
    try {
      window.localStorage.setItem(SIDEBAR_PINNED_KEY, pinned ? '1' : '0');
    } catch {
      /* ignore */
    }
    return () => {
      root.style.setProperty('--sidebar-width', SIDEBAR_WIDTH_EXPANDED);
      root.removeAttribute('data-sidebar-rail');
      root.removeAttribute('data-sidebar-expanded');
    };
  }, [pinned, sidebarExpanded]);

  const onSidebarMouseEnter = () => {
    if (!pinned) setRailHovered(true);
  };

  const onSidebarMouseLeave = () => {
    if (!pinned) setRailHovered(false);
  };

  const togglePin = () => {
    setPinned((prev) => {
      const next = !prev;
      if (!next) setRailHovered(false);
      return next;
    });
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
      path: '/dashboard',
    },
    ...(showLecturesNav
      ? [
          {
            id: 'meetings',
            label: T.meetings(),
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            ),
            path: '/meetings',
          },
        ]
      : []),
    ...(isWorkplace
      ? [
          {
            id: 'tasks',
            label: 'Tasks',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            ),
            path: '/insights',
          },
          {
            id: 'insights',
            label: 'Insights',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            ),
            path: '/insights',
          },
          {
            id: 'search',
            label: 'Search',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            ),
            path: '/transcripts',
          },
          {
            id: 'calendar',
            label: 'Calendar',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            ),
            path: '/meetings',
          },
          {
            id: 'integrations',
            label: 'Integrations',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v4" />
                <path d="M12 18v4" />
                <path d="M4.93 4.93l2.83 2.83" />
                <path d="M16.24 16.24l2.83 2.83" />
                <path d="M2 12h4" />
                <path d="M18 12h4" />
                <path d="M4.93 19.07l2.83-2.83" />
                <path d="M16.24 7.76l2.83-2.83" />
              </svg>
            ),
            path: '/settings',
          },
        ]
      : []),
    ...(isEducation
      ? [
          {
            id: 'quiz-results',
            label: 'Quiz Results',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            ),
            path: '/quiz-results',
          },
        ]
      : []),
    ...(isEducationAdmin
      ? [
          {
            id: 'courses',
            label: 'Courses',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 7.5L12 3l9 4.5-9 4.5-9-4.5z" />
                <path d="M7 10.5V15c0 1.8 2.2 3.2 5 3.2s5-1.4 5-3.2v-4.5" />
              </svg>
            ),
            path: '/courses',
          },
        ]
      : []),
    ...(isEducationAdmin
      ? [
          {
            id: 'teachers',
            label: 'Teachers',
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <path d="M20 8v6" />
                <path d="M23 11h-6" />
              </svg>
            ),
            path: '/teachers',
          },
        ]
      : []),
    {
      id: 'settings',
      label: 'Settings',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ),
      path: '/settings',
    },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Sliding active-item pill: measured (not CSS-only) because nav items live in a
  // plain flex column, so the pill's target position/height are read straight off
  // the active button's own layout box rather than assumed from index.
  const navRef = useRef(null);
  const itemRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ opacity: 0 });

  // Depend on plain values only. `menuItems` and `isActive` are rebuilt on every render, so
  // listing them as dependencies re-ran this effect after each of its own state updates and
  // React stopped the app with "Maximum update depth exceeded".
  const activeItemId = (menuItems.find((item) => isActive(item.path)) || {}).id || '';
  const menuItemCount = menuItems.length;

  useLayoutEffect(() => {
    const nav = navRef.current;
    const el = activeItemId ? itemRefs.current[activeItemId] : null;
    if (!nav || !el) {
      setPillStyle((prev) => (prev.opacity === 0 ? prev : { opacity: 0 }));
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = Math.round(elRect.top - navRect.top);
    const height = Math.round(elRect.height);
    setPillStyle((prev) =>
      prev.opacity === 1 && prev.top === top && prev.height === height ? prev : { top, height, opacity: 1 }
    );
  }, [activeItemId, menuItemCount, sidebarExpanded]);

  const logout = () => {
    try {
      window.localStorage.removeItem('clientAdminToken');
    } catch (e) {
      /* ignore */
    }
    try {
      delete axios.defaults.headers.common.Authorization;
    } catch (e) {
      /* ignore */
    }
    navigate('/admin-login', { replace: true });
  };

  return (
    <aside
      className={`sidebar${pinned ? ' sidebar--pinned' : ' sidebar--rail'}${
        !pinned && railHovered ? ' sidebar--hovered' : ''
      }`}
      aria-label="Main navigation"
      data-pinned={pinned ? 'true' : 'false'}
      onMouseEnter={onSidebarMouseEnter}
      onMouseLeave={onSidebarMouseLeave}
    >
      <div className="sidebar__surface">
        <div className="sidebar__brand">
          <button type="button" className="sidebar__brand-btn" onClick={() => navigate('/dashboard')}>
            <img
              src="/assets/portiq-mark.png"
              alt=""
              className="sidebar__brand-mark"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="sidebar__label sidebar__brand-name portiq-wordmark">Port<b>IQ</b></span>
          </button>
          {sidebarExpanded ? (
            <button
              type="button"
              className="sidebar__pin-btn"
              onClick={togglePin}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              aria-pressed={pinned}
              title={pinned ? 'Unpin — icons only' : 'Pin open'}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill={pinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 17v5" />
                <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a3 3 0 0 0-6 0v3.76z" />
              </svg>
            </button>
          ) : null}
        </div>

        <nav className="sidebar__nav" ref={navRef}>
          <div className="sidebar__active-pill" style={pillStyle} aria-hidden />
          {menuItems.map((item) => (
            <button
              key={item.id}
              ref={(node) => {
                itemRefs.current[item.id] = node;
              }}
              type="button"
              className={`sidebar-item${isActive(item.path) ? ' active' : ''}`}
              onClick={() => navigate(item.path)}
              title={!sidebarExpanded ? item.label : undefined}
            >
              {item.icon}
              <span className="sidebar__label">{item.label}</span>
            </button>
          ))}
        </nav>

        {isWorkplace && FEATURE_INTERVIEW_UI ? (
          <button
            type="button"
            className="sidebar-promo"
            onClick={() => navigate('/interview')}
            title={!sidebarExpanded ? 'Interview Mode' : undefined}
          >
            <span className="sidebar-promo__icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 11h-6" />
                <path d="M19 8v6" />
              </svg>
            </span>
            <span className="sidebar-promo__body">
              <strong>Interview Mode</strong>
              <span>Structured hiring evaluations from recordings.</span>
              <em>Explore →</em>
            </span>
          </button>
        ) : null}

        <div className="sidebar__footer">
          {(accountName || accountEmail) && (
            <button
              type="button"
              className="sidebar-account"
              onClick={() => navigate('/settings')}
              title={!sidebarExpanded ? [accountName, accountEmail].filter(Boolean).join(' · ') : undefined}
            >
              <span className="sidebar-account__avatar" aria-hidden>
                {accountInitial}
              </span>
              <span className="sidebar-account__body sidebar__label">
                <strong className="sidebar-account__name">{accountName || accountEmail}</strong>
                <span className="sidebar-account__meta">{accountRoleLabel}</span>
              </span>
            </button>
          )}
          {role !== 'faculty' && (
            <button
              type="button"
              className="sidebar-footer-btn"
              onClick={() => navigate('/admin')}
              title={!sidebarExpanded ? 'Admin' : undefined}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              <span className="sidebar__label">Admin</span>
            </button>
          )}
          <button
            type="button"
            className="sidebar-footer-btn sidebar-footer-btn--logout"
            onClick={logout}
            title={!sidebarExpanded ? 'Log out' : undefined}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="sidebar__label">Log out</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
